export const M10_READING_PRACTICE = {
  id: "m10-reading-practice",
  title: "Módulo 10 — Reading Practice",
  topic: "Reported Speech · Reported Questions · If/Whether · Indirect Questions · Wishes & Regrets",
  iconType: "book",

  sets: [

    // ===================================================================
    // READING SET 1 — UNIT 11: REPORTED SPEECH (STATEMENTS)
    // ===================================================================
    {
      id: "m10-rp-set1",
      title: "Reading 1: What They Said",
      context: `*Unit 11 — Reported speech: statements*
*Grammar focus: said (that) / told + object + (that) / tense backshift*`,

      questions: [

        {
          type: "passage",
          passage: `**The Interview**

Maya was nervous before her job interview. Her friend told her that she would do well. Her mother said that she was proud of her. Her mentor told her that she had prepared enough. Maya wrote all three sentences in her notebook and read them before she walked through the door.`
        },
        {
          type: "mc",
          q: "What did Maya's mentor tell her?",
          options: [
            "That she had prepared enough.",
            "That she would be nervous.",
            "That she should study more."
          ],
          a: "That she had prepared enough.",
          tip: "Reported speech: 'you have prepared enough' → she had prepared enough (tense backshift)."
        },
        {
          type: "mc",
          q: "Why did Maya write the sentences in her notebook?",
          options: [
            "To remember the encouraging words before going in.",
            "To practise her grammar.",
            "To report them to someone else."
          ],
          a: "To remember the encouraging words before going in.",
          tip: "The passage says she read them before she walked through the door — she used them for confidence."
        },

        {
          type: "passage",
          passage: `**After the Interview**

Maya called her friend that evening. She said that the interview had gone well. She told her that the manager had asked her about her experience. She also said that she had felt calm for most of it. Her friend replied that she had known she would be fine.`
        },
        {
          type: "mc",
          q: "What did Maya say about the interview?",
          options: [
            "That it had gone well and she had felt calm.",
            "That it had been terrible and she had panicked.",
            "That the manager had not asked any questions."
          ],
          a: "That it had gone well and she had felt calm.",
          tip: "Two reported statements: 'it had gone well' + 'she had felt calm'. Both past perfect after said/told."
        },
        {
          type: "mc",
          q: "Which verb introduces reported speech when the listener is mentioned?",
          options: [
            "Told — 'she told her that...'",
            "Said — 'she said her that...'",
            "Both said and told can be followed directly by an object."
          ],
          a: "Told — 'she told her that...'",
          tip: "TOLD + object + that. SAID + that (no object). 'She told her that...' ✅ 'She said her that...' ❌"
        },

        {
          type: "passage",
          passage: `**The Offer**

Three days later, Maya received an email. She called her mother immediately. She said that they had offered her the job. She told her that the salary was good and that she could start the following month. Her mother said that she had always known Maya would succeed.`
        },
        {
          type: "mc",
          q: "Identify the tense backshift in: 'She said that they had offered her the job.'",
          options: [
            "Direct: 'They have offered me the job.' → Reported: had offered (present perfect → past perfect).",
            "Direct: 'They offered me the job.' → Reported: had offered (past simple → past perfect).",
            "Both A and B could produce 'had offered' depending on context."
          ],
          a: "Both A and B could produce 'had offered' depending on context.",
          tip: "Both present perfect (have offered) and past simple (offered) backshift to past perfect (had offered) in reported speech."
        },
        {
          type: "mc",
          q: "The following month' in reported speech replaces which time expression?",
          options: [
            "'Next month' in direct speech.",
            "'Last month' in direct speech.",
            "'This month' in direct speech."
          ],
          a: "'Next month' in direct speech.",
          tip: "Time expression backshift: next month → the following month. A key reported speech rule!"
        },

        {
          type: "passage",
          passage: `**Her First Week**

After her first week, Maya's colleague asked how she was finding it. She said that everyone had been very welcoming. She told him that the work was challenging but interesting. She also said that she felt she was learning something new every day.`
        },
        {
          type: "mc",
          q: "Why does 'feel' NOT backshift to 'had felt' in 'she felt she was learning something new every day'?",
          options: [
            "Because it describes a present and ongoing truth at the time of speaking.",
            "Because 'feel' is irregular and doesn't backshift.",
            "Because the sentence uses 'also said', which prevents backshift."
          ],
          a: "Because it describes a present and ongoing truth at the time of speaking.",
          tip: "Backshift is optional when the statement is still true at the time of reporting. 'She felt she was learning' — the learning is ongoing."
        },
        {
          type: "mc",
          q: "Which sentence correctly reports: 'Everyone is very welcoming.'",
          options: [
            "She said that everyone had been very welcoming.",
            "She said that everyone is very welcoming.",
            "She said that everyone was being very welcoming."
          ],
          a: "She said that everyone had been very welcoming.",
          tip: "Is (present simple) → was / had been (past). Both are possible, but 'had been' shows full backshift."
        },

        {
          type: "passage",
          passage: `**A Year Later**

At her one-year review, Maya's manager said that she had exceeded all expectations. He told her that the team respected her and that she had become an important part of the company. Maya later told her mentor that it had been the best year of her working life.`
        },
        {
          type: "mc",
          q: "What did Maya's manager tell her at the review?",
          options: [
            "That she had exceeded expectations and that the team respected her.",
            "That she needed to work harder next year.",
            "That she had not met the company's standards."
          ],
          a: "That she had exceeded expectations and that the team respected her.",
          tip: "Two reported statements linked by 'and that' — a common pattern for reporting multiple things."
        },
        {
          type: "mc",
          q: "What did Maya tell her mentor afterwards?",
          options: [
            "That it had been the best year of her working life.",
            "That she had been nervous all year.",
            "That she wanted to change jobs."
          ],
          a: "That it had been the best year of her working life.",
          tip: "'It has been the best year' → 'it had been the best year' (present perfect → past perfect backshift)."
        }
      ]
    },

    // ===================================================================
    // READING SET 2 — UNIT 12: REPORTED QUESTIONS
    // ===================================================================
    {
      id: "m10-rp-set2",
      title: "Reading 2: What They Asked",
      context: `*Unit 12 — Reported questions*
*Grammar focus: asked + WH-word / if / whether + subject + verb (no inversion, no auxiliary)*`,

      questions: [

        {
          type: "passage",
          passage: `**The New Student**

On his first day, Daniel arrived late to the language class. The teacher asked him why he was late. She also asked whether he had the course materials. Daniel replied that he had left them at home. The teacher asked him if he understood the classroom rules.`
        },
        {
          type: "mc",
          q: "What two things did the teacher ask Daniel?",
          options: [
            "Why he was late, and whether he had the course materials.",
            "Where he lived, and whether he liked the class.",
            "What his name was, and if he had done the homework."
          ],
          a: "Why he was late, and whether he had the course materials.",
          tip: "WH-question: 'asked him why he was late.' Yes/no question: 'asked whether he had the materials.'"
        },
        {
          type: "mc",
          q: "Which structure is INCORRECT for a reported yes/no question?",
          options: [
            "She asked if he had the materials.",
            "She asked whether he had the materials.",
            "She asked did he have the materials."
          ],
          a: "She asked did he have the materials.",
          tip: "No inversion in reported questions! 'Asked did he have' ❌ — the auxiliary DO disappears and word order becomes subject + verb."
        },

        {
          type: "passage",
          passage: `**The Language Exchange**

Sofia was looking for a conversation partner. She posted an ad online. Someone called Leo replied. He asked her what language she wanted to practise. He also asked whether she had any previous experience with language exchanges. Sofia asked him where he had learned his English.`
        },
        {
          type: "mc",
          q: "What did Leo ask Sofia in his message?",
          options: [
            "What language she wanted to practise, and whether she had experience with exchanges.",
            "Where she lived, and how old she was.",
            "Whether she spoke French, and if she wanted to meet."
          ],
          a: "What language she wanted to practise, and whether she had experience with exchanges.",
          tip: "WH reported question: 'asked what language she wanted.' Yes/no: 'asked whether she had any experience.'"
        },
        {
          type: "mc",
          q: "What did Sofia ask Leo in return?",
          options: [
            "Where he had learned his English.",
            "Whether he spoke Spanish.",
            "How many languages he knew."
          ],
          a: "Where he had learned his English.",
          tip: "WH reported question: 'where had you learned your English?' → 'where he had learned his English' (backshift + no inversion)."
        },

        {
          type: "passage",
          passage: `**The First Meeting**

Leo and Sofia met at a café. Leo asked her how long she had been studying Spanish. Sofia asked him if he had ever visited Spain. He said he hadn't, but that he hoped to go someday. She asked whether he preferred speaking or writing in a foreign language.`
        },
        {
          type: "mc",
          q: "What did Leo ask Sofia about her Spanish?",
          options: [
            "How long she had been studying it.",
            "Whether she had a Spanish teacher.",
            "What level she was at."
          ],
          a: "How long she had been studying it.",
          tip: "'How long have you been studying Spanish?' → 'how long she had been studying Spanish' (backshift: present perfect continuous → past perfect continuous)."
        },
        {
          type: "mc",
          q: "What did Sofia ask Leo near the end of the passage?",
          options: [
            "Whether he preferred speaking or writing in a foreign language.",
            "If he wanted to meet again.",
            "How many times he had tried to learn Spanish."
          ],
          a: "Whether he preferred speaking or writing in a foreign language.",
          tip: "Yes/no question with two options: whether + subject + verb (no inversion). 'Do you prefer...' → 'whether he preferred...'"
        },

        {
          type: "passage",
          passage: `**Progress**

After two months, Sofia's teacher asked the class if anyone had noticed an improvement in their speaking. Sofia raised her hand. Afterwards, her teacher asked her who she had been practising with. Sofia told her about Leo. The teacher asked whether they had considered joining the school's conversation club.`
        },
        {
          type: "mc",
          q: "What did Sofia's teacher ask the class at the start of the passage?",
          options: [
            "If anyone had noticed an improvement in their speaking.",
            "Whether they had done their homework.",
            "Who had the best pronunciation in the group."
          ],
          a: "If anyone had noticed an improvement in their speaking.",
          tip: "Yes/no reported question: 'Has anyone noticed...?' → 'asked if anyone had noticed...' (backshift: present perfect → past perfect)."
        },
        {
          type: "mc",
          q: "What did the teacher ask Sofia personally?",
          options: [
            "Who she had been practising with.",
            "How many hours she studied each week.",
            "Whether she wanted to become a teacher."
          ],
          a: "Who she had been practising with.",
          tip: "WH reported question: 'Who have you been practising with?' → 'who she had been practising with' (past perfect continuous)."
        },

        {
          type: "passage",
          passage: `**The Certificate**

At the end of the year, Leo received a language certificate. He texted Sofia and asked her if she had received hers too. She replied that she had, and asked him whether he felt his English had truly improved. He told her that he did — and asked if she wanted to start learning Italian together.`
        },
        {
          type: "mc",
          q: "What did Leo ask Sofia in his text message?",
          options: [
            "If she had received her certificate too.",
            "Whether she wanted to celebrate.",
            "How she had done in her exam."
          ],
          a: "If she had received her certificate too.",
          tip: "Yes/no reported question: 'Have you received yours?' → 'asked her if she had received hers too.'"
        },
        {
          type: "mc",
          q: "What did Leo ask at the very end of the passage?",
          options: [
            "If she wanted to start learning Italian together.",
            "Whether she had enjoyed the year.",
            "Where the best Italian classes were."
          ],
          a: "If she wanted to start learning Italian together.",
          tip: "'Do you want to start learning Italian?' → 'asked if she wanted to start learning Italian.' Yes/no question with if."
        }
      ]
    },

    // ===================================================================
    // READING SET 3 — UNIT 13: IF / WHETHER + VERB+PREPOSITION
    // ===================================================================
    {
      id: "m10-rp-set3",
      title: "Reading 3: Wondering and Deciding",
      context: `*Unit 13 — If/Whether as subject/object clauses · Verb + preposition collocations*
*Grammar focus: wonder if · know whether · decide whether · care about · dream of · believe in · etc.*`,

      questions: [

        {
          type: "passage",
          passage: `**The Decision**

Carla had been offered a job in another city. She didn't know whether to accept it. She kept wondering if the move would be worth it. She believed in taking risks, but she also cared deeply about staying close to her family. She couldn't decide whether to go or stay.`
        },
        {
          type: "mc",
          q: "What two things did Carla struggle with?",
          options: [
            "Whether to accept the job, and whether to go or stay near her family.",
            "Whether to study more, and whether to ask for more money.",
            "If she liked the city, and whether her family would visit her."
          ],
          a: "Whether to accept the job, and whether to go or stay near her family.",
          tip: "KNOW WHETHER + DECIDE WHETHER — two embedded clauses expressing her uncertainty. CARE ABOUT + BELIEVE IN — verb + preposition collocations."
        },
        {
          type: "mc",
          q: "Which verb + preposition collocation appears in the passage?",
          options: [
            "Cared about and believed in.",
            "Dreamed of and thought about.",
            "Insisted on and relied on."
          ],
          a: "Cared about and believed in.",
          tip: "CARE ABOUT (staying close to family) + BELIEVE IN (taking risks) — both verb + preposition collocations from Unit 13."
        },

        {
          type: "passage",
          passage: `**Her Friend's Advice**

Carla spoke to her friend Niamh. Niamh asked whether Carla had thought about what she really wanted. She said she had always dreamed of living somewhere new. Niamh told her that she needed to think about what she would regret more — going, or not going.`
        },
        {
          type: "mc",
          q: "What did Niamh say Carla had always done?",
          options: [
            "Dreamed of living somewhere new.",
            "Thought about moving abroad.",
            "Believed in staying close to family."
          ],
          a: "Dreamed of living somewhere new.",
          tip: "DREAM OF + gerund — verb + preposition collocation. Reported speech: 'she had always dreamed of living somewhere new.'"
        },
        {
          type: "mc",
          q: "What question did Niamh ask Carla?",
          options: [
            "Whether she had thought about what she really wanted.",
            "If she had already accepted the offer.",
            "Whether her family approved of the decision."
          ],
          a: "Whether she had thought about what she really wanted.",
          tip: "Reported question: WHETHER + subject + verb. 'Had you thought about what you really want?' → 'whether she had thought about what she really wanted.'"
        },

        {
          type: "passage",
          passage: `**The Pros and Cons**

That night, Carla made a list. She thought about what she would gain and what she would lose. She wondered if she would cope with being alone in a new city. She wasn't sure whether the people there would be welcoming. But she also thought about what she had always wanted from life.`
        },
        {
          type: "mc",
          q: "What did Carla wonder?",
          options: [
            "If she would cope with being alone in a new city.",
            "Whether the salary was good enough.",
            "If her boss would give her more time to decide."
          ],
          a: "If she would cope with being alone in a new city.",
          tip: "WONDER IF — embedded clause expressing internal uncertainty. 'Will I cope?' → 'wondered if she would cope.'"
        },
        {
          type: "mc",
          q: "What structure is 'She wasn't sure whether the people there would be welcoming'?",
          options: [
            "WHETHER as an object clause after an adjective phrase (not sure whether).",
            "WHETHER as a subject clause at the start of the sentence.",
            "WHETHER introducing a direct question."
          ],
          a: "WHETHER as an object clause after an adjective phrase (not sure whether).",
          tip: "NOT SURE WHETHER — adjective + whether clause. The embedded clause functions as the object of 'wasn't sure.'"
        },

        {
          type: "passage",
          passage: `**The Answer**

In the end, Carla decided to go. She realised that she had always believed in following her instincts. She called the company and told them she would start the following month. That night, she texted Niamh: "I've decided. I'm going." Niamh replied: "I knew you would."`,
        },
        {
          type: "mc",
          q: "What did Carla realise about herself?",
          options: [
            "That she had always believed in following her instincts.",
            "That she had always cared about staying close to home.",
            "That she had never dreamed of leaving her city."
          ],
          a: "That she had always believed in following her instincts.",
          tip: "BELIEVE IN + gerund — reported realisation. 'I have always believed in following my instincts' → 'she had always believed in following her instincts.'"
        },
        {
          type: "mc",
          q: "What does Niamh's reply 'I knew you would' imply?",
          options: [
            "That she had always expected Carla to take the risk.",
            "That she was surprised by the decision.",
            "That she wanted to go with Carla."
          ],
          a: "That she had always expected Carla to take the risk.",
          tip: "'I knew you would' = 'I knew you would decide to go.' Niamh had expected this — consistent with her advice to follow her dreams."
        },

        {
          type: "passage",
          passage: `**Six Months Later**

Carla had settled in. She wrote to Niamh and said that she didn't regret the decision at all. She said that she had thought about coming back once — but then she had gone for a walk in the new city and realised she was exactly where she needed to be. She told Niamh she was proud of herself for going.`
        },
        {
          type: "mc",
          q: "What did Carla tell Niamh about her decision?",
          options: [
            "That she didn't regret it at all.",
            "That she had regretted it at first.",
            "That she was thinking about coming back."
          ],
          a: "That she didn't regret it at all.",
          tip: "Reported statement: 'I don't regret it' → 'she didn't regret the decision at all.' Present simple → past simple (backshift)."
        },
        {
          type: "mc",
          q: "What made Carla realise she was in the right place?",
          options: [
            "Going for a walk in the new city.",
            "Receiving a message from her family.",
            "Getting positive feedback at work."
          ],
          a: "Going for a walk in the new city.",
          tip: "The passage states: 'she had gone for a walk in the new city and realised she was exactly where she needed to be.'"
        }
      ]
    },

    // ===================================================================
    // READING SET 4 — UNIT 14: INDIRECT QUESTIONS
    // ===================================================================
    {
      id: "m10-rp-set4",
      title: "Reading 4: Asking Politely",
      context: `*Unit 14 — Indirect questions*
*Grammar focus: Could you tell me... / Do you know... / I was wondering... / Would you mind telling me...*`,

      questions: [

        {
          type: "passage",
          passage: `**At the Information Desk**

James had just arrived in a new city. He went to the information desk at the train station. He asked the assistant if she could tell him where the nearest hotel was. He also asked whether she knew what time the museums opened. The assistant smiled and answered both questions.`
        },
        {
          type: "mc",
          q: "What two things did James ask at the information desk?",
          options: [
            "Where the nearest hotel was, and what time the museums opened.",
            "How far the city centre was, and whether there was a tourist map.",
            "When the next train left, and where the taxi rank was."
          ],
          a: "Where the nearest hotel was, and what time the museums opened.",
          tip: "Two indirect questions: 'Could you tell me where...?' + 'Do you know what time...?' — polite embedded form with subject + verb (no inversion)."
        },
        {
          type: "mc",
          q: "Why did James use indirect questions instead of direct ones?",
          options: [
            "To sound more polite in a formal public context.",
            "Because he didn't know the correct direct question forms.",
            "Because indirect questions are always shorter."
          ],
          a: "To sound more polite in a formal public context.",
          tip: "Indirect questions (Could you tell me / Do you know) are used in formal or polite contexts — a key function of this structure."
        },

        {
          type: "passage",
          passage: `**The Hotel Check-In**

At the hotel, James approached the reception desk. He asked the receptionist if she could tell him whether breakfast was included. He also wondered aloud whether she could let him know what the WiFi password was. The receptionist told him both and said she hoped he would enjoy his stay.`
        },
        {
          type: "mc",
          q: "What did James ask the receptionist?",
          options: [
            "Whether breakfast was included, and what the WiFi password was.",
            "Whether the room had a sea view, and where the restaurant was.",
            "What time checkout was, and whether he could upgrade his room."
          ],
          a: "Whether breakfast was included, and what the WiFi password was.",
          tip: "Two indirect questions: 'if she could tell him WHETHER breakfast was included' + 'could let him know WHAT the WiFi password was.'"
        },
        {
          type: "mc",
          q: "What phrase does James use to introduce his second question politely?",
          options: [
            "He wondered aloud whether she could let him know...",
            "He said directly: 'What is the WiFi password?'",
            "He asked: 'Do you know the WiFi password?'"
          ],
          a: "He wondered aloud whether she could let him know...",
          tip: "I was wondering whether... / He wondered whether... — a softening indirect structure used in polite requests."
        },

        {
          type: "passage",
          passage: `**At the Museum**

James visited the city museum. He asked a guide if she could tell him who had painted the large portrait in the entrance hall. He also asked whether she knew how old the building was. The guide told him the painter's name and said that the building was over three hundred years old.`
        },
        {
          type: "mc",
          q: "What two indirect questions did James ask the museum guide?",
          options: [
            "Who had painted the portrait, and how old the building was.",
            "Where the portrait had come from, and when the museum had opened.",
            "Whether the museum had a café, and what time it closed."
          ],
          a: "Who had painted the portrait, and how old the building was.",
          tip: "WH indirect questions: 'Could you tell me WHO had painted...' + 'Do you know HOW OLD the building was?' Subject + verb order, no inversion."
        },
        {
          type: "mc",
          q: "What would be the DIRECT form of 'He asked if she could tell him who had painted the portrait'?",
          options: [
            "'Who painted the large portrait?'",
            "'Could you tell me who painted the portrait?' is already indirect.",
            "Both A and B — A is direct, B is indirect."
          ],
          a: "Both A and B — A is direct, B is indirect.",
          tip: "'Who painted the portrait?' = direct. 'Could you tell me who painted it?' = indirect. Same information, different levels of formality."
        },

        {
          type: "passage",
          passage: `**A Friendly Conversation**

That evening, James had dinner at a local restaurant. He got talking to the owner. He asked him if he knew where the best viewpoint in the city was. The owner asked James if he could tell him where he was from. They talked for an hour. James said later that it had been the best part of the trip.`
        },
        {
          type: "mc",
          q: "What did James ask the restaurant owner?",
          options: [
            "If he knew where the best viewpoint in the city was.",
            "Whether he would recommend a local dish.",
            "If he could tell him about the history of the city."
          ],
          a: "If he knew where the best viewpoint in the city was.",
          tip: "Indirect question: 'Do you know where the best viewpoint is?' → 'asked him if he knew where the best viewpoint was.' (backshift: is → was)."
        },
        {
          type: "mc",
          q: "What did the owner ask James?",
          options: [
            "If he could tell him where he was from.",
            "Whether he had visited the city before.",
            "What he thought of the food."
          ],
          a: "If he could tell him where he was from.",
          tip: "Indirect question: 'Could you tell me where you are from?' → 'asked James if he could tell him where he was from.' Polite form used both ways!"
        },

        {
          type: "passage",
          passage: `**The Review**

Back home, James wrote a travel review. He wrote: "I would recommend asking locals for advice. Most people are happy to help if you ask politely. Try saying 'Do you know where...' or 'Could you tell me...' instead of asking directly. People respond much better to polite questions."`
        },
        {
          type: "mc",
          q: "What does James recommend in his review?",
          options: [
            "Using indirect questions to ask locals politely for advice.",
            "Using direct questions because they are faster.",
            "Avoiding speaking to locals and using maps instead."
          ],
          a: "Using indirect questions to ask locals politely for advice.",
          tip: "James recommends 'Do you know where...' and 'Could you tell me...' — the two core indirect question starters from Unit 14."
        },
        {
          type: "mc",
          q: "According to James's review, why do indirect questions work better?",
          options: [
            "Because people respond much better to polite questions.",
            "Because they are grammatically more correct than direct questions.",
            "Because they are shorter and easier to understand."
          ],
          a: "Because people respond much better to polite questions.",
          tip: "James's own conclusion: 'People respond much better to polite questions.' The social function of indirect questions!"
        }
      ]
    },

    // ===================================================================
    // READING SET 5 — UNIT 15: WISHES & REGRETS
    // ===================================================================
    {
      id: "m10-rp-set5",
      title: "Reading 5: If Only",
      context: `*Unit 15 — Wishes and regrets*
*Grammar focus: wish + past simple (present) · wish + past perfect (past) · if only · wish vs hope*`,

      questions: [

        {
          type: "passage",
          passage: `**The Painter Who Became an Accountant**

Tom had studied accounting because his parents had told him it was practical. Now, at forty, he wished he had chosen a different path. He wished he worked with colour and light instead of numbers. If only he had listened to his art teacher, who had told him he had real talent.`
        },
        {
          type: "mc",
          q: "What are Tom's two wishes?",
          options: [
            "A past regret (hadn't studied accounting) and a present wish (worked with colour and light).",
            "Two present wishes about his current job.",
            "Two past regrets about decisions he made at school."
          ],
          a: "A past regret (hadn't studied accounting) and a present wish (worked with colour and light).",
          tip: "WISH + HAD CHOSEN = past regret. WISH + WORKED = present wish (he doesn't work with colour NOW). Two different structures, two different time frames."
        },
        {
          type: "mc",
          q: "What does 'if only he had listened to his art teacher' tell us?",
          options: [
            "He didn't listen — it's an emphatic past regret.",
            "He did listen, but it made no difference.",
            "He is currently thinking about listening to advice."
          ],
          a: "He didn't listen — it's an emphatic past regret.",
          tip: "IF ONLY + past perfect = emphatic past regret. The reality is the OPPOSITE: he didn't listen to his art teacher."
        },

        {
          type: "passage",
          passage: `**Tom's Lunch Break**

Every day, Tom ate his lunch in the park near his office. He watched the artists who set up their easels by the fountain. He wished he could sit with them. He wished he didn't have to go back to his desk at one o'clock. He hoped that one day things would be different.`
        },
        {
          type: "mc",
          q: "What is the difference between Tom's wishes and his hope in this passage?",
          options: [
            "The wishes are about current unreal situations. The hope is about a real future possibility.",
            "The wishes are about the past. The hope is about the present.",
            "There is no difference — wish and hope mean the same thing."
          ],
          a: "The wishes are about current unreal situations. The hope is about a real future possibility.",
          tip: "WISH + COULD / WISH + DIDN'T HAVE TO = current unreal. HOPE + WOULD = real future possibility. Wish = unreal. Hope = still possible!"
        },
        {
          type: "mc",
          q: "What does 'He wished he didn't have to go back to his desk' tell us about reality?",
          options: [
            "He does have to go back to his desk every day.",
            "He never has to go back to his desk.",
            "He went back to his desk reluctantly once."
          ],
          a: "He does have to go back to his desk every day.",
          tip: "WISH + DIDN'T = current unreal — the reality is the OPPOSITE: he DOES have to go back. Present wish = past simple = unreal NOW."
        },

        {
          type: "passage",
          passage: `**The Evening Class**

Tom's wife noticed how unhappy he was. She suggested he try an evening art class. Tom said he wasn't sure whether it was too late to start. But he went. The teacher told him that he had natural talent. That night, Tom didn't wish he were someone else. He hoped he had found something real.`
        },
        {
          type: "mc",
          q: "What did Tom say about starting the evening class?",
          options: [
            "He wasn't sure whether it was too late.",
            "He didn't know if he had the right materials.",
            "He wondered whether his wife would come with him."
          ],
          a: "He wasn't sure whether it was too late.",
          tip: "NOT SURE WHETHER — embedded clause from Unit 13, combining naturally with Unit 15 themes. 'Whether' = if, in a polite/uncertain context."
        },
        {
          type: "mc",
          q: "What changed for Tom at the end of the passage?",
          options: [
            "He stopped wishing and started hoping — he had found something real.",
            "He decided to quit his accounting job immediately.",
            "He wished he had started the class years earlier."
          ],
          a: "He stopped wishing and started hoping — he had found something real.",
          tip: "The shift from WISH (unreal) to HOPE (possible) signals Tom's change of perspective. Hope = belief in possibility. This is the emotional core of Unit 15."
        },

        {
          type: "passage",
          passage: `**Two Years Later**

Tom still worked as an accountant. But on weekends, he painted. He had sold three paintings. His teacher asked him if he had ever considered going professional. Tom said he didn't know whether he was good enough. But he hoped he was. And for now, that was enough.`
        },
        {
          type: "mc",
          q: "What did Tom's teacher ask him?",
          options: [
            "If he had ever considered going professional.",
            "Whether he wanted to teach art himself.",
            "How long he had been painting."
          ],
          a: "If he had ever considered going professional.",
          tip: "Reported yes/no question (Unit 12): 'Have you ever considered going professional?' → 'asked him if he had ever considered going professional.'"
        },
        {
          type: "mc",
          q: "What do Tom's final words — 'he hoped he was. And for now, that was enough' — suggest?",
          options: [
            "That hope, even without certainty, can be enough to keep going.",
            "That Tom had given up on his dream.",
            "That Tom was certain he would become a professional painter."
          ],
          a: "That hope, even without certainty, can be enough to keep going.",
          tip: "The final message of Unit 15 and the module: HOPE (real possibility) vs WISH (unreal). Tom doesn't know — but he hopes. And that is different from giving up."
        },

        {
          type: "passage",
          passage: `**What He Told His Son**

One evening, Tom's teenage son told him he was thinking of studying law because it was 'safe'. Tom said he didn't want to tell him what to do. But he told him one thing: that he hoped his son would choose the life he truly wanted — not the one that simply made sense on paper.`
        },
        {
          type: "mc",
          q: "What did Tom tell his son?",
          options: [
            "That he hoped his son would choose the life he truly wanted.",
            "That studying law was a mistake.",
            "That he wished he had studied law himself."
          ],
          a: "That he hoped his son would choose the life he truly wanted.",
          tip: "Reported speech (Unit 11) + HOPE (Unit 15): 'He told him that he hoped his son would choose...' — two units combining in one sentence."
        },
        {
          type: "mc",
          q: "Why does Tom use HOPE (not WISH) when talking to his son?",
          options: [
            "Because his son's future is still open — it's a real possibility, not an unreal one.",
            "Because wish is only used for the speaker's own desires.",
            "Because Tom doesn't care which career his son chooses."
          ],
          a: "Because his son's future is still open — it's a real possibility, not an unreal one.",
          tip: "HOPE = still possible. His son hasn't decided yet — the future is open. If Tom said 'I wish he would choose...', it would imply it's impossible or unlikely. HOPE is right here."
        }
      ]
    }
  ]
};
